/**
 * Members tab — shown inside CardDetailSlideOver. Phase 5 surface:
 *
 *   - List of invited users (avatar + name + role badge).
 *   - "+ Invite" inline picker (workspace member dropdown + role select).
 *   - Inviter / founder / admin can revoke; everyone else gets read-only.
 *
 * The picker only shows workspace members who aren't already invited
 * (and aren't the card creator). Invitations write to chaos.card_invitees
 * via /api/kanban/cards/:cardId/invitations.
 */
import type { InviteRole } from '@chaos/types/project';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  invitationKeys,
  invitationsApi,
  workspaceMemberKeys,
  workspaceMembersApi,
} from '../../lib/api/invitations';
import { useAuthStore } from '../../stores/auth-store';
import { Button } from '../ui/button';

interface CardMembersTabProps {
  cardId: string;
}

const ROLE_LABEL_KEYS: Record<InviteRole, string> = {
  editor: 'invitations.role_editor',
  commenter: 'invitations.role_commenter',
  viewer: 'invitations.role_viewer',
};

export function CardMembersTab({ cardId }: CardMembersTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const workspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const canManage =
    role === 'founder' || role === 'admin' || role === 'employee' || role === 'tuner';

  const invitationsQ = useQuery({
    queryKey: invitationKeys.list(cardId),
    queryFn: () => invitationsApi.list(cardId),
  });
  const wsMembersQ = useQuery({
    queryKey: workspaceId ? workspaceMemberKeys.list(workspaceId) : ['noop'],
    queryFn: () => (workspaceId ? workspaceMembersApi.list(workspaceId) : Promise.resolve([])),
    enabled: !!workspaceId,
  });

  const inviteM = useMutation({
    mutationFn: (input: { userId: string; role: InviteRole }) =>
      invitationsApi.invite(cardId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationKeys.list(cardId) }),
  });
  const revokeM = useMutation({
    mutationFn: (targetUserId: string) => invitationsApi.revoke(cardId, targetUserId),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationKeys.list(cardId) }),
  });

  const invitedIds = new Set((invitationsQ.data ?? []).map((i) => i.userId));
  const eligible = (wsMembersQ.data ?? []).filter(
    (m) => !invitedIds.has(m.userId) && m.userId !== userId,
  );

  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [selectedRole, setSelectedRole] = React.useState<InviteRole>('commenter');
  const [picker, setPicker] = React.useState(false);

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-muted-foreground">{t('invitations.tab_subtitle')}</p>

      <ul className="divide-y divide-border rounded border border-border">
        {invitationsQ.isLoading && (
          <li className="px-4 py-3 text-[13px] text-muted-foreground">{t('common.loading')}</li>
        )}
        {!invitationsQ.isLoading && invitationsQ.data?.length === 0 && (
          <li className="px-4 py-3 text-[13px] text-muted-foreground">
            {t('invitations.no_invitees')}
          </li>
        )}
        {invitationsQ.data?.map((inv) => (
          <li key={inv.id} className="group/row flex items-center gap-3 px-4 py-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px] font-medium text-foreground">
                {inv.displayName ?? inv.email}
              </span>
              <span className="truncate text-[11px] text-muted-foreground">{inv.email}</span>
            </div>
            <span className="rounded bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground">
              {t(
                ROLE_LABEL_KEYS[(inv.role as InviteRole) ?? 'viewer'] ?? 'invitations.role_viewer',
              )}
            </span>
            {canManage && (
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm(`Revoke ${inv.email}'s access?`)) {
                    await revokeM.mutateAsync(inv.userId);
                  }
                }}
                aria-label={t('invitations.revoke')}
                className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/5 hover:text-error group-hover/row:opacity-100"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>

      {canManage && picker && eligible.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-[13px]"
            aria-label={t('invitations.select_user')}
          >
            <option value="">{t('invitations.select_user')}</option>
            {eligible.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as InviteRole)}
            className="rounded border border-border bg-background px-2 py-1 text-[13px]"
            aria-label={t('projects.select_role')}
          >
            <option value="editor">{t('invitations.role_editor')}</option>
            <option value="commenter">{t('invitations.role_commenter')}</option>
            <option value="viewer">{t('invitations.role_viewer')}</option>
          </select>
          <Button
            variant="default"
            size="sm"
            disabled={!selectedUserId || inviteM.isPending}
            onClick={async () => {
              if (!selectedUserId) return;
              await inviteM.mutateAsync({
                userId: selectedUserId,
                role: selectedRole,
              });
              setSelectedUserId('');
              setPicker(false);
            }}
          >
            {inviteM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {t('invitations.send')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedUserId('');
              setPicker(false);
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {canManage && picker && eligible.length === 0 && (
        <p className="text-[12px] text-muted-foreground">{t('projects.no_eligible_users')}</p>
      )}

      {canManage && !picker && (
        <Button variant="ghost" size="sm" onClick={() => setPicker(true)}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {t('invitations.invite_cta')}
        </Button>
      )}
    </div>
  );
}
